import { Box, Flex, Image, Text, VStack } from "@chakra-ui/react";
import { BaseModal } from "@picovico/ui";
import { useAppSelector } from "store/hooks";
import { WebConfig } from "types/index";
import { MdIosShare } from "react-icons/md";
import { ReactNode } from "react";

/**
 * Instructions dialog for installing the PWA on iOS Safari, which has no
 * `beforeinstallprompt`/native install flow — users must add to the Home Screen
 * manually. The instruction copy is fixed; only the look & feel is tenant-driven
 * via `layout_config.install_config` (mirrors `offline_config`).
 */

interface IosInstallDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const IosInstallDialog = ({ isOpen, onClose }: IosInstallDialogProps) => {
  const config: WebConfig = useAppSelector((state) => state.config);
  const cfg = config.layout_config?.install_config;
  const appName = config.company_info?.name || config.title || "this app";

  const accent = cfg?.accent_color || "primary.500";
  const title = cfg?.title || `Install ${appName}`;

  const steps: { text: ReactNode }[] = [
    {
      text: (
        <>
          Tap the{" "}
          <Box
            as={MdIosShare}
            display="inline-block"
            verticalAlign="text-bottom"
            color={accent}
            aria-label="Share"
          />{" "}
          <Text as="span" fontWeight="semibold">
            Share
          </Text>{" "}
          button in Safari&rsquo;s toolbar.
        </>
      ),
    },
    {
      text: (
        <>
          Scroll down and choose{" "}
          <Text as="span" fontWeight="semibold">
            &ldquo;Add to Home Screen&rdquo;
          </Text>
          .
        </>
      ),
    },
    {
      text: (
        <>
          Tap{" "}
          <Text as="span" fontWeight="semibold">
            Add
          </Text>{" "}
          &mdash; the app icon appears on your Home Screen.
        </>
      ),
    },
  ];

  return (
    <BaseModal isOpen={isOpen} onClose={onClose} title={title}>
      <Box bg={cfg?.background} pb={2}>
        <VStack align="stretch" spacing={5}>
          {cfg?.image && (
            <Image
              src={cfg.image}
              alt={title}
              maxH="10rem"
              objectFit="contain"
              alignSelf="center"
            />
          )}

          <Text color={cfg?.message_color} opacity={cfg?.message_color ? 1 : 0.85}>
            Add {appName} to your Home Screen to use it like an app, including
            offline.
          </Text>

          <VStack align="stretch" spacing={4}>
            {steps.map((step, i) => (
              <Flex key={i} align="center" gap={3}>
                <Flex
                  flexShrink={0}
                  align="center"
                  justify="center"
                  boxSize="1.75rem"
                  borderRadius="full"
                  bg={accent}
                  color="white"
                  fontWeight="bold"
                  fontSize="sm"
                >
                  {i + 1}
                </Flex>
                <Text color={cfg?.message_color}>{step.text}</Text>
              </Flex>
            ))}
          </VStack>
        </VStack>
      </Box>
    </BaseModal>
  );
};

export default IosInstallDialog;
